import React, { RefObject } from "react";
import { Svg, SvgSize } from "../../../../Shared/Assets/Svg/React";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonType
} from "../../../../Shared/Buttons/React";
import { Modal } from "../../../../Shared/Modal/React";
import { useProvetStatic, useTranslationContext } from "../../context";
import { formatAppointmentDate, formatAppointmentTime } from "../../utilities";

interface ProvetCancellationSuccessModalProps {
  active: boolean;
  appointmentTime?: string;
  appointmentDate?: string;
  departmentName?: string;
  onClose: () => void;
  contentRef: RefObject<HTMLDivElement>;
  modalRef?: RefObject<HTMLDivElement>;
}

export const ProvetCancellationSuccessModal = ({
  active,
  appointmentTime,
  appointmentDate,
  departmentName,
  onClose,
  contentRef,
  modalRef
}: ProvetCancellationSuccessModalProps) => {
  const translationContext = useTranslationContext();
  // The site language, so that the date is not shown in the browser's or default language
  const { language } = useProvetStatic();
  const translations = translationContext?.translations;
  const titleId = "provet-cancellation-success-modal-title";

  const date = appointmentDate ? formatAppointmentDate(appointmentDate, language) : "";
  const time = appointmentTime ? formatAppointmentTime(appointmentTime) : "";

  return (
    <Modal
      active={active}
      onClose={onClose}
      hasFooter={false}
      ref={modalRef}
      titleId={titleId}
      modalName="cancellationSuccess"
    >
      <div ref={contentRef} className="flex flex-col gap-2xl w-full text-t-neutral-800">
        {/* Modal Header */}
        <div className="flex flex-col gap-md">
          <h2
            id={titleId}
            className="flex items-center font-guardian text-dark-blue text-t-md sm:text-t-xl font-t-semi-bold leading-lg sm:leading-xxl gap-xs md:gap-sm"
          >
            <Svg
              href="/svg/system/check.svg"
              size={SvgSize.XXL}
              additionalClass="text-success-200"
            />
            {translations?.cancelSuccessTitle}
          </h2>
        </div>

        {/* Success Message */}
        <div>
          <p className="text-md md:text-lg text-t-neutral-800">
            {translations?.cancelSuccessMessage}
          </p>
        </div>

        {/* Cancelled Booking Details */}
        <div className="p-lg bg-neutral-100 rounded-sm">
          <h3 className="mb-xs font-guardian text-distinct-xs font-distinct text-dark-blue uppercase md:text-distinct-sm">
            {translations?.cancelledBookingDetails}
          </h3>
          <div className="flex flex-col gap-xxs text-t-sm md:text-t-md">
            <div className="flex items-center gap-xs">
              <span className="font-t-semi-bold">{translations?.time}:</span>
              <span>{date}</span>
              <span className="inline-flex w-xxs h-xxs bg-t-neutral-200 rounded-full" />
              <span>{time}</span>
            </div>
            {departmentName && (
              <div className="flex items-center gap-xs">
                <span className="font-t-semi-bold">{translations?.location}:</span>
                <span>{departmentName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Close Button */}
        <div className="flex flex-col sm:flex-row gap-md">
          <Button
            type={ButtonType.Button}
            size={ButtonSize.LG}
            color={ButtonColor.Turquoise}
            onClick={onClose}
            data-online-booking-action="confirm"
          >
            {translations?.closeAndStartOver}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
